import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Modal,
  useTheme,
  Tooltip,
  Button,
  CardContent,
  Stack,
  CardActions,
  Skeleton,
  Link as MuiLink,
} from "@mui/material";
import { FiArrowUpRight } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import { ImInfo } from "react-icons/im";
import { Link, useLocation, useParams } from "react-router-dom";
import { logEvent } from "services/amplitude.service.js";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import useCategoryData from "../hooks/use-category-data.hook";
import CategoryIcon from "./CategoryIcon";
import { fontSize } from "@mui/system";
import { useSelector } from "react-redux";
import { categoryDescriptions } from "data/category-description";

function TooltipContent({ description }) {
  if (Array.isArray(description)) {
    return (
      <Stack direction="column">
        {description.map((item, index) => (
          <Typography key={`category-desc-${index}`} variant="body1">
            {item}
          </Typography>
        ))}
      </Stack>
    );
  }
  return <Typography variant="body1">{description}</Typography>;
}

export default function SidebarCard({ category, link, loading, resultCount, totalResults }) {
  const [open, setOpen] = useState(false);
  // const [bgIcon, setBgIcon] = useState("");
  const theme = useTheme();
  const isMobile = useIsMobile();
  const {source} = useParams();
  const isMediaBias = source === "rating";  
  const { pathname } = useLocation();
  const isPublishersPage = pathname.includes("/partner-publishers");
  const categoryName = category.categoryName_chr || category.label;
  // Sections built from local config (databases, for example) carry no
  // description_chr from the API, so fall back to the bundled copy.
  const description =
    category.description_chr || categoryDescriptions[categoryName];
  // const { getCategoryIcon } = useCategoryData(() => {});

  // useEffect(() => {
  //   setBgIcon(getCategoryIcon(category.categoryName_chr));
  // }, [category, getCategoryIcon]);

  const handleInfoClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const modalStyle = {
    display: "flex",
    flexDirection: "column",
    width: "40rem",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 3,
    outline: "none",
    borderRadius: "2rem",
    gap: 1.2,
  };

  const handleClick = () => {
    logEvent("click_category_card", {
      category: categoryName,
    });
  };

  if (loading && !isMediaBias)
    return (
      <Skeleton
        sx={{
          position: "relative",
          minWidth: { xs: "100%", sm: "23vw", md: "17vw" },
          minHeight: 0,
          backgroundColor: theme.palette.secondary.main,
          borderRadius: 1,
        }}
        variant="rectangular"
        height="25vh"
      />
    );

  return (
    <React.Fragment>
      <Card
        className="tour-cat-section-card"
        elevation={0}
        sx={{
          position: "relative",
          minWidth: { xs: "100%", sm: "23vw", md: "17vw" },
          maxWidth: { xs: "100%", sm: "23vw", md: "17vw" },
          minHeight: 0,
          backgroundColor: theme.palette.secondary.main,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          wordBreak: "break-word",
        }}
      >
        <CategoryIcon
          iconId={categoryName}
          sx={{
            fill: theme.palette.secondary.dark,
            position: "absolute",
            fontSize: { xs: "20rem", lg: "30rem" },
            right: { xs: "-7rem", lg: "-10rem" },
            bottom: { xs: "-7rem", lg: "-10rem" },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 4.4,
            right: 5.4,
            display: "flex",
            zIndex: 10,
            "& > svg": {
              color: theme.palette.secondary.dark,
            },
            "&:hover > svg": {
              color: theme.palette.primary.dark,
            },
          }}
        >
          <Tooltip
            title={
              !isMobile ? (
                <TooltipContent description={description} />
              ) : null
            }
            placement="right"
            arrow
          >
            <Box>
              {!isPublishersPage && 
                <ImInfo
                  onClick={handleInfoClick}
                  size={16}
                  style={{
                    p: 1,
                  }}
                />
              }
            </Box>
          </Tooltip>
        </Box>
        <CardContent>
          <Typography
            component="div"
            sx={{ fontSize: { xs: "2rem", lg: "2.3rem", breakWord: "break" } }}
            key={category}
            color="primary.dark"
            fontFamily={theme.typography.h3.fontFamily}
            fontWeight={theme.typography.h3.fontWeight}
          >
            {categoryName}
          </Typography>
          {String(categoryName).toLowerCase() === "ebsco" && (
            <Typography variant="body2" sx={{ mt: 2, position: "relative" }}>
              Click{" "}
              <MuiLink
                href="https://connect.ebsco.com/s/article/Searching-Tips-for-K-12-and-Public-Library-Users-Finding-Better-Results-Faster?language=en_US"
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
              >
                here
              </MuiLink>{" "}
              for EBSCO search tips
            </Typography>
          )}
        </CardContent>
        <CardActions
          onClick={handleClick}
          sx={{
            flexDirection: Number.isFinite(totalResults) ? "column" : "row",
            alignItems: "flex-start",
            gap: 0.5,
          }}
        >
          {!loading && Number.isFinite(totalResults) && totalResults >= 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min(resultCount || 0, totalResults).toLocaleString("en-US")} of {totalResults.toLocaleString("en-US")} results
            </Typography>
          )}
          <Button
            className="tour-cat-section-card-see-all"
            variant="text"
            size="small"
            to={link}
            component={Link}
            startIcon={<FiArrowUpRight />}
            color="inherit"
          >
            See all
          </Button>
        </CardActions>
      </Card>
      {isMobile && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ImInfo color="secondary.dark" size={15} />
                <Typography>About this category</Typography>
              </Box>
              <AiOutlineClose
                onClick={handleClose}
                size={18}
                style={{
                  cursor: "pointer",
                  color: theme.palette.secondary.dark,
                }}
              />
            </Box>
            <Box sx={{ display: "block" }}>
              <Typography variant="h4" color="primary.dark">
                {categoryName}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: 1 }}>
            </Box>
          </Box>
        </Modal>
      )}
    </React.Fragment>
  );
}
